import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Edit, Venue, Stage } from "shared/assets/icons";
import { VENUE_PATHS } from "Venues/router";
import { SideMenu } from "shared/components";
import { copy } from "shared/assets";
import { IMenuItemType, ISideMenuItem } from "shared/components/SideMenu/types";
import { getStageActions, getVenueActions, useVenueRoute } from "Venues/utils";
import { useStagePageContext, useVenuePageContext } from "Venues/contexts";
import { API_ENDPOINTS } from "shared/constants";
import {
  clearActionMeta,
  dataMetaSelector,
  editVenueStageOrder,
} from "Venues/state";
import { VENUE_ACTIONS } from "Venues/types";
import { IAPIState, IStatus } from "shared/types";
import { addTemporaryToast } from "shared/components/Toast";

import {
  getVenueAddMenuItems,
  getStageAddMenuItems,
  getStageSubItems,
  getAddedInfoBlockItems,
} from "./data";
import { buildUrl } from "shared/utils";

const { venuePage, errors, editVenue } = copy;

export const VenueSideMenu = () => {
  const {
    venue,
    venueId,
    venueStages,
    userPermissions,
    openDeleteStageModal,
    openDeleteVenueModal,
    openDeleteInfoBlockModal,
    venueInfoBlocks,
    brandColor,
    openSuggestEditModal,
  } = useVenuePageContext();
  const {
    stage,
    stageId,
    equipmentCategories,
    stageInfoBlocks,
    openSuggestStageEditModal,
  } = useStagePageContext();
  const { pushRoute, getPath } = useVenueRoute();

  const history = useHistory();
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.editVenueStageOrder)
  );
  const [disableUpdateList, setDisableUpdateList] = useState(false);

  const title: string = useMemo(() => {
    if (stage) {
      return stage.title;
    }
    return venue?.title || "";
  }, [venue, stage]);

  const items: ISideMenuItem[] = useMemo(() => {
    if (venue && title !== undefined) {
      let items: ISideMenuItem[] = [];

      // manage equipment
      if (userPermissions.EDIT) {
        items.push({
          id: "editEquipment",
          title: venuePage.edit.equipment.short,
          icon: Edit,
          path: getPath(VENUE_PATHS.EDIT_VENUE_EQUIPMENT),
          type: IMenuItemType.action,
        });
      }

      // venue and venue pages
      items.push({
        title: venue?.title,
        id: venueId,
        icon: Venue,
        path: getPath(VENUE_PATHS.VENUE),
        actions: getVenueActions({
          venueId,
          history,
          userPermissions,
          openDeleteVenueModal,
          openSuggestEditModal,
          pushRoute,
        }),
        matchPaths: [
          buildUrl(VENUE_PATHS.VENUE, {
            venueId,
          }),
          buildUrl(VENUE_PATHS.CREATE_VENUE_STAGE, {
            venueId,
          }),
        ],
        subItems: getAddedInfoBlockItems({
          getPath: (infoBlockSlug: string) =>
            getPath(VENUE_PATHS.VENUE, {
              hash: infoBlockSlug,
            }),
          getEditPath: (infoBlockSlug: string) =>
            getPath(VENUE_PATHS.EDIT_VENUE_INFO_BLOCK, {
              params: { infoBlockSlug },
            }),
          blocks: venueInfoBlocks,
          userPermissions,
          handleEdit: (infoBlockSlug: string) =>
            pushRoute(VENUE_PATHS.EDIT_VENUE_INFO_BLOCK, {
              params: { infoBlockSlug },
            }),
          handleDelete: ({ id, type }) =>
            openDeleteInfoBlockModal({
              venue: API_ENDPOINTS.VENUE(venueId),
              id,
              type,
            }),
          openSuggestEditModal: (props) =>
            openSuggestEditModal({ ...props, type: "VenueInfoBlock" }),
        }),
      });

      // stages and stage pages
      venueStages.forEach((stage) => {
        const stageId = stage.id;
        items.push({
          title: stage.title,
          id: stageId,
          icon: Stage,
          path: getPath(VENUE_PATHS.STAGE, { stageId }),
          matchPaths: [
            buildUrl(VENUE_PATHS.STAGE, {
              venueId,
              stageId,
            }),
            ...equipmentCategories.map(({ slug: categorySlug }) =>
              buildUrl(
                VENUE_PATHS.STAGE,
                {
                  venueId,
                  stageId,
                },
                categorySlug
              )
            ),
          ],
          type: IMenuItemType.draggable,
          subItems: getStageSubItems({
            venueId,
            stageId,
            blocks: stageInfoBlocks,
            equipmentCategories,
            userPermissions,
            getPath,
            pushRoute,
            handleEdit: (infoBlockSlug: string) =>
              pushRoute(VENUE_PATHS.EDIT_STAGE_INFO_BLOCK, {
                stageId,
                params: { infoBlockSlug },
              }),
            handleDelete: ({ id, type }) =>
              openDeleteInfoBlockModal({
                stage: API_ENDPOINTS.STAGE(stageId),
                id,
                type,
              }),
            openSuggestStageEditModal,
          }),
          actions: getStageActions({
            userPermissions,
            stageId,
            openDeleteStageModal,
            openSuggestStageEditModal,
            pushRoute,
          }),
        });
      });

      return items;
    }
    return [];
  }, [
    equipmentCategories,
    getPath,
    history,
    openDeleteInfoBlockModal,
    openDeleteStageModal,
    openDeleteVenueModal,
    openSuggestEditModal,
    openSuggestStageEditModal,
    pushRoute,
    stageInfoBlocks,
    title,
    userPermissions,
    venue,
    venueId,
    venueInfoBlocks,
    venueStages,
  ]);
  const [orderedList, setOrderedList] = useState<ISideMenuItem[]>(items);

  // update ordered list when items change
  useEffect(() => {
    if (items) {
      setOrderedList(items);
    }
  }, [items]);

  useEffect(() => {
    if (
      apiMeta?.status &&
      [IAPIState.succeeded, IAPIState.failed].includes(apiMeta?.status)
    ) {
      const toast =
        apiMeta?.status === IAPIState.succeeded
          ? { status: IStatus.success, message: editVenue.stageOrder.success }
          : { status: IStatus.error, message: errors.general };

      dispatch(addTemporaryToast(toast));
      dispatch(clearActionMeta(VENUE_ACTIONS.editVenueStageOrder));

      setOrderedList(items);
      setDisableUpdateList(false);
    }
  }, [apiMeta?.status, items, dispatch]);

  const onDragEnd = (orderedList: ISideMenuItem[]) => {
    setDisableUpdateList(true);
    setOrderedList(orderedList);

    // list of draggable items ids in new order
    const orderedDraggableIdList = orderedList.reduce((list, { type, id }) => {
      if (type === IMenuItemType.draggable) {
        return [...list, id];
      }
      return list;
    }, [] as string[]);

    dispatch(
      editVenueStageOrder({ sortedIdList: orderedDraggableIdList, venueId })
    );
  };

  if (venue === null) return null;
  return (
    <SideMenu
      type={stage === null ? "venue" : "stage"}
      titleUrl={
        stage === null ? getPath(VENUE_PATHS.VENUE) : getPath(VENUE_PATHS.STAGE)
      }
      title={title}
      addItems={
        stage === null
          ? getVenueAddMenuItems({ blocks: venueInfoBlocks, getPath })
          : getStageAddMenuItems({
              stageId: stageId || "",
              blocks: stageInfoBlocks,
              getPath,
            })
      }
      items={orderedList}
      showAddMenu={userPermissions.EDIT}
      canDragItems={userPermissions.EDIT}
      dragTooltipCopy={editVenue.stageOrder.tooltip}
      onDragEnd={onDragEnd}
      accentColor={brandColor}
      disableUpdateList={disableUpdateList}
    />
  );
};
